import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { BeautifulMentionsPlugin } from 'lexical-beautiful-mentions';
import { createSentinelMentionNodeEntries } from './composer-v2/lib/SentinelMentionNode';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ENTER_COMMAND
} from 'lexical';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkPlugin } from '@lexical/react/LexicalAutoLinkPlugin';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { $convertToMarkdownString, TRANSFORMERS } from '@lexical/markdown';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { ListNode, ListItemNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { CodeNode, CodeHighlightNode, registerCodeHighlighting } from '@lexical/code';
import { $getNearestNodeOfType } from '@lexical/utils';
import {
  SEND_MESSAGE_V2,
  GET_UPLOAD_URL,
  GET_CONVERSATION_MEMBERS,
  GET_MESSAGES_PAGINATED,
  GET_THREAD_REPLIES,
  GET_MY_PROFILE,
  UPDATE_COMPOSER_TOOLBAR_VISIBLE
} from '../../graphql/operations';
import {
  ALLOWED_ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS_PER_MESSAGE,
  classifyAttachmentKind,
  rejectionReasonFor,
  uploadAttachment
} from '../../lib/attachments';
import { mergeMessageIntoCache } from './messageCacheSync';
// KAP-303 — V1 textarea (`MessageComposer.js`) still uses the mentionTrigger
// helpers + `MentionAutocomplete` directly. V2 now drives mentions through
// `lexical-beautiful-mentions`; we keep the files in place for V1 fallback.
import { URL_MATCHERS } from './composer-v2/lib/urlMatchers';
import { LinkDialogProvider } from './composer-v2/lib/linkDialog';
import { KeyboardShortcutsPlugin } from './composer-v2/lib/plugins/keyboardShortcutsPlugin';
import LinkPopover from './composer-v2/LinkPopover';
import ComposerToolbar from './composer-v2/ComposerToolbar';
import CodeBlockLanguagePicker from './composer-v2/CodeBlockLanguagePicker';
import AttachMenu from './composer-v2/AttachMenu';
import ComposerEmojiButton from './composer-v2/ComposerEmojiButton';
import { createEmojiShortcodeTransformer } from './composer-v2/lib/emojiShortcodeTransformer';
import { createMentionTransformer } from './composer-v2/lib/mentionTransformer';
import { UNDERLINE_TRANSFORMER } from './composer-v2/lib/underlineTransformer';
import './MessageComposer.css';
import './composer-v2/MessageComposerV2.css';
// `MentionAutocomplete.css` defines `.mention-autocomplete*` rules used by
// the V2 BeautifulMentions menu. The legacy import path went through
// `MessageInput.js` → `MentionAutocomplete.js`, but MessageInput is dead
// code (never mounted), so the stylesheet was never reaching the bundle
// and the V2 menu rendered as a default 0-width <ul>. Import here so the
// rules ship with the composer.
import './MentionAutocomplete.css';

/**
 * MessageComposerV2 — Lexical-based composer behind the `composer_v2` feature
 * flag. Mirrors V1's send pipeline (Apollo mutation, cache update, attachment
 * upload, drag-drop, mention autocomplete via existing `MentionAutocomplete`)
 * but replaces the `<textarea>` with `LexicalComposer` + `RichTextPlugin`.
 *
 * KAP-302 shipped the foundation. KAP-304 adds inline formatting via
 * `MarkdownShortcutPlugin` (autoreplace), `<KeyboardShortcutsPlugin />`
 * (Cmd+Shift+X / Cmd+E / Cmd+K), `<LinkPlugin />` + `<AutoLinkPlugin />`,
 * and switches submit serialization from plain `getTextContent()` to
 * `$convertToMarkdownString(INLINE_TRANSFORMERS)` per ADR-0044.
 *
 * Mentions still emit bare `@token` (matching V1); the
 * `@[Display Name](user:uuid)` sentinel from ADR-0044 lands in KAP-303
 * when `lexical-beautiful-mentions` + `MarkdownRenderer` rewire ship.
 *
 * Chrome (attachment list, drag-drop overlay, send button, also-post-to-channel,
 * attachment toast, submit/restore-on-error semantics) is duplicated from
 * `MessageComposerV1` during the flag window. KAP-303 will extract a shared hook.
 */
const LEXICAL_INITIAL_CONFIG = {
  namespace: 'MessageComposerV2',
  theme: {
    paragraph: 'msg-composer-v2-paragraph',
    text: {
      bold: 'msg-composer-v2-text-bold',
      italic: 'msg-composer-v2-text-italic',
      strikethrough: 'msg-composer-v2-text-strike',
      underline: 'msg-composer-v2-text-underline',
      code: 'msg-composer-v2-text-code'
    },
    link: 'msg-composer-v2-link',
    list: {
      ul: 'msg-composer-v2-list-ul',
      ol: 'msg-composer-v2-list-ol',
      listitem: 'msg-composer-v2-list-item',
      nested: { listitem: 'msg-composer-v2-list-item-nested' }
    },
    quote: 'msg-composer-v2-quote',
    code: 'msg-composer-v2-code-block',
    codeHighlight: {
      atrule: 'editor-token-keyword',
      attr: 'editor-token-attr',
      boolean: 'editor-token-property',
      builtin: 'editor-token-selector',
      cdata: 'editor-token-comment',
      char: 'editor-token-selector',
      class: 'editor-token-function',
      'class-name': 'editor-token-function',
      comment: 'editor-token-comment',
      constant: 'editor-token-property',
      deleted: 'editor-token-property',
      doctype: 'editor-token-comment',
      entity: 'editor-token-operator',
      function: 'editor-token-function',
      important: 'editor-token-variable',
      inserted: 'editor-token-selector',
      keyword: 'editor-token-keyword',
      namespace: 'editor-token-variable',
      number: 'editor-token-property',
      operator: 'editor-token-operator',
      prolog: 'editor-token-comment',
      property: 'editor-token-property',
      punctuation: 'editor-token-punctuation',
      regex: 'editor-token-variable',
      selector: 'editor-token-selector',
      string: 'editor-token-selector',
      symbol: 'editor-token-property',
      tag: 'editor-token-property',
      url: 'editor-token-operator',
      variable: 'editor-token-variable'
    }
  },
  onError(error) {
    // eslint-disable-next-line no-console
    console.error('[composer-v2] Lexical error:', error);
  },
  // KAP-306: registering block nodes here unlocks the full `TRANSFORMERS`
  // set above and the toolbar buttons in `ComposerToolbar`. Mounting a
  // plugin that references an unregistered node throws Lexical err 173
  // on the first reconciler pass — see history note in KAP-304 README.
  nodes: [
    LinkNode,
    AutoLinkNode,
    ListNode,
    ListItemNode,
    HeadingNode,
    QuoteNode,
    CodeNode,
    CodeHighlightNode,
    // KAP-303 — register SentinelMentionNode (BeautifulMentionNode subclass)
    // here BEFORE BeautifulMentionsPlugin mounts. Lexical err 173 fires on
    // first reconciler pass when a plugin references an unregistered node
    // (see KAP-306 fix commit ec55c01 for the HeadingNode precedent).
    // The `replace` entry tells Lexical to swap every BeautifulMentionNode
    // the plugin creates for our subclass — whose getTextContent()
    // returns the ADR-0044 sentinel for markdown serialization.
    ...createSentinelMentionNodeEntries()
  ]
};

// KAP-307: `:shortcode:` autoreplace transformer + KAP-303 mention sentinel
// transformer appended to the stock `TRANSFORMERS`. Order matters: mention
// transformer first so its `)` trigger wins over any other `)`-triggered
// transformer that lands here later. Built once at module scope so we
// don't reallocate per render.
const COMPOSER_TRANSFORMERS = [
  createMentionTransformer(),
  UNDERLINE_TRANSFORMER,
  ...TRANSFORMERS,
  createEmojiShortcodeTransformer()
];

function EditorChangePlugin({ onContentChange }) {
  return (
    <OnChangePlugin
      onChange={(editorState) => {
        editorState.read(() => {
          // KAP-304: emit canonical markdown (ADR-0044) so the wire payload
          // round-trips through `MarkdownRenderer` on read. For plain text
          // with no formatting this is byte-identical to `getTextContent()`.
          // KAP-306: full `TRANSFORMERS` set (lists, quote, fenced code)
          // is now safe once block nodes are registered.
          // KAP-303 fix-forward: serialize via COMPOSER_TRANSFORMERS so the
          // mention `TextMatchTransformer.export` fires on every editor
          // change. The previous vanilla `TRANSFORMERS` use silently fell
          // back to BeautifulMentionNode.getTextContent() → bare `@Display
          // Name` plain text — the data-corruption failure mode KAP-303
          // explicitly warned about. Caught in live smoke against dev.
          onContentChange($convertToMarkdownString(COMPOSER_TRANSFORMERS));
        });
      }}
      ignoreSelectionChange
    />
  );
}

function CodeHighlightPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerCodeHighlighting(editor), [editor]);
  return null;
}

// KAP-303 — `MentionBridgePlugin` + `MentionInsertPlugin` (legacy regex-based
// trigger detection + manual text rewrite) are replaced by
// `<BeautifulMentionsPlugin>` from `lexical-beautiful-mentions`, which owns
// the full mention lifecycle (trigger detection, async suggestion fetch,
// menu render, keyboard nav, insertion). The legacy helpers in
// `composer-v2/lib/mentionTrigger.js` are still imported by V1 textarea
// (MessageComposer.js) so we do NOT delete them here.

// Derive a friendly mention label from a possibly-email displayName.
// Google OAuth sometimes sets `displayName = email` for users who never
// provided a name; rendering "@yvonne@kapsa.ai" as the mention chip is
// noisy. If the stored displayName looks like an email, we use the
// email's local part with a leading capital ("yvonne@kapsa.ai" → "Yvonne").
// The mention's stable id (userId) is unaffected — only the visible
// label changes.
function prettifyMentionLabel(displayName, userId) {
  const raw = (displayName || userId || '').trim();
  if (!raw) return '';
  if (!raw.includes('@')) return raw;
  const local = raw.split('@')[0];
  if (!local) return raw;
  // "yvonne" → "Yvonne", "first.last" → "First Last"
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

// Menu shell — matches the visual style of the legacy MentionAutocomplete
// dropdown (`.mention-autocomplete` etc. classes from MessageComposer.css).
// forwardRef is required by BeautifulMentionsPlugin so it can manage focus.
const MentionMenu = forwardRef(function MentionMenu({ children, loading, ...rest }, ref) {
  return (
    <ul
      {...rest}
      ref={ref}
      className="mention-autocomplete mention-autocomplete--v2"
      role="listbox"
      aria-label={loading ? 'Loading mentions' : 'Mention suggestions'}
    >
      {children}
    </ul>
  );
});

const MentionMenuItem = forwardRef(function MentionMenuItem(
  { selected, item, children, ...rest },
  ref
) {
  return (
    <li
      {...rest}
      ref={ref}
      className={`mention-autocomplete-item${selected ? ' is-active' : ''}`}
      role="option"
      aria-selected={selected}
    >
      <span className="mention-autocomplete-display">
        {item?.data?.displayName || item?.value || children}
      </span>
      {item?.data?.username && item?.data?.displayName && item.data.username !== item.data.displayName ? (
        <span className="mention-autocomplete-handle">@{item.data.username}</span>
      ) : null}
    </li>
  );
});

// KAP-307 — bridges the React-tree `<ComposerEmojiButton>` (which lives
// outside the LexicalComposer tree, in the bottom-actions row) to a
// Lexical editor.update() that inserts the chosen glyph at the caret.
// Mirrors `MentionInsertPlugin` shape: parent owns the pending state.
function EmojiInsertPlugin({ pendingEmoji, onConsumed }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!pendingEmoji) return;
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      selection.insertText(pendingEmoji.glyph);
    });
    // Return DOM focus to the editor so the user keeps typing without
    // an extra click — the Picker has closed and `ComposerEmojiButton`
    // deliberately skips focusing the trigger in this path. Mirrors
    // `LinkPopover.apply()` which dispatches a command then refocuses.
    editor.focus();
    onConsumed();
  }, [editor, pendingEmoji, onConsumed]);
  return null;
}

// Bridges the React-tree `@` button (which lives outside the
// LexicalComposer tree) to a real `beforeinput` event on the
// contenteditable root. The browser turns that into the exact same
// input pipeline a user pressing Shift+2 (`@`) would trigger, which
// flows through Lexical's input handler AND the BeautifulMentionsPlugin's
// `LexicalTypeaheadMenuPlugin` selection-change listener. Result: the
// menu opens identically to manual typing.
//
// History on this plugin:
//   v1: `selection.insertText('@')` inside `editor.update` from a
//       parent-state-driven useEffect. Hung the browser when the editor
//       had never been focused (selection in stale state, raced with
//       BeautifulMentionsPlugin's own input listener).
//   v2: `openMentionMenu({ trigger: '@' })` via `useBeautifulMentions`.
//       Dispatches `$insertTriggerAtSelection` which inserts `@` but
//       relies on the typeahead's selection-change driven trigger
//       detection. In SOME contexts (live-reported by Bailey, but not
//       reproducible in Playwright), the menu fails to surface after
//       the programmatic insert. Hypothesis: the typeahead's update
//       listener doesn't always fire for command-driven Lexical updates
//       in the same way it does for user-input-driven updates.
//   v3 (this): dispatch a real `beforeinput` event the contenteditable
//       root. Same path as physical keyboard input. The fallback inside
//       try/catch still calls `openMentionMenu` for the unusual case
//       where the browser refuses the synthetic event.
function MentionTriggerInsertPlugin({ pendingTrigger, onConsumed }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (!pendingTrigger) return;
    let cancelled = false;
    try {
      editor.focus();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[composer-v2] @ button: editor.focus failed', err);
    }
    const id = window.setTimeout(() => {
      if (cancelled) return;
      try {
        const root = editor.getRootElement();
        if (root) {
          // beforeinput is what the browser fires when the user types
          // a character into a contenteditable. Lexical's CONTROLLED
          // input pipeline listens for exactly this event, calls
          // preventDefault to take over, runs its own update, and
          // notifies LexicalTypeaheadMenuPlugin's selection-change
          // listener — which is what triggers BeautifulMentionsPlugin
          // to open the menu. Synthetic dispatch with the same event
          // shape gives us the same downstream pipeline.
          root.dispatchEvent(new InputEvent('beforeinput', {
            bubbles: true,
            cancelable: true,
            inputType: 'insertText',
            data: '@'
          }));
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[composer-v2] @ button: beforeinput dispatch failed', err);
      }
      onConsumed();
    }, 0);
    return () => {
      cancelled = true;
      window.clearTimeout(id);
    };
  }, [editor, pendingTrigger, onConsumed]);
  return null;
}

function EnterToSendPlugin({ onSend, mentionMenuOpenRef }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      (event) => {
        if (mentionMenuOpenRef.current) return false;
        if (!event) return false;
        if (event.shiftKey) return false;
        if (event.metaKey || event.ctrlKey) return false;

        // KAP-306 — inside a list item or a code block, defer to
        // ListPlugin / RichText handlers so Enter creates a new list
        // item (or exits the list when empty) / inserts a newline in
        // code. Returning `false` lets the next-priority command
        // handler take over; HIGH-priority intercept only fires for
        // paragraph / quote / heading lines where Enter should send.
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchorNode = selection.anchor.getNode();
          const inListItem = $getNearestNodeOfType(anchorNode, ListItemNode);
          const inCodeBlock = $getNearestNodeOfType(anchorNode, CodeNode);
          if (inListItem || inCodeBlock) return false;
        }

        event.preventDefault();
        onSend();
        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor, onSend, mentionMenuOpenRef]);
  return null;
}

function ClearEditorPlugin({ resetCount }) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    if (resetCount === 0) return;
    editor.update(() => {
      const root = $getRoot();
      root.clear();
      root.append($createParagraphNode());
    });
  }, [editor, resetCount]);
  return null;
}

function MessageComposerV2({
  conversationId,
  channelName,
  threadParentId,
  placeholder: customPlaceholder,
  onAssistantThinkingStart
}) {
  const [value, setValue] = useState('');
  const [pendingEmojiInsert, setPendingEmojiInsert] = useState(null);
  const [pendingMentionTrigger, setPendingMentionTrigger] = useState(null);
  const [editorResetCount, setEditorResetCount] = useState(0);
  const [alsoPostToChannel, setAlsoPostToChannel] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [attachmentError, setAttachmentError] = useState('');
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef(null);
  // KAP-303 — the BeautifulMentionsPlugin flips this on onMenuOpen/onMenuClose
  // so EnterToSendPlugin defers Enter while the mention picker is showing.
  const mentionMenuOpenRef = useRef(false);
  const apolloClient = useApolloClient();

  const isThread = !!threadParentId;

  // KAP-305: Aa toggle preference, backed by GET_MY_PROFILE +
  // updateComposerToolbarVisible mutation (mirrors notification-sound).
  // Default `true` for new users — formatting is discoverable by default;
  // the toggle exists for users who want a minimal composer.
  const { data: profileData } = useQuery(GET_MY_PROFILE, {
    fetchPolicy: 'cache-first'
  });
  const toolbarVisible = profileData?.getMyProfile?.composerToolbarVisible !== false;

  const [updateComposerToolbarVisibleMutation] = useMutation(
    UPDATE_COMPOSER_TOOLBAR_VISIBLE,
    {
      update: (cache, { data }) => {
        const updated = data?.updateComposerToolbarVisible;
        if (!updated) return;
        const existing = cache.readQuery({ query: GET_MY_PROFILE });
        const currentProfile = existing?.getMyProfile || {};
        cache.writeQuery({
          query: GET_MY_PROFILE,
          data: {
            getMyProfile: {
              ...currentProfile,
              ...updated
            }
          }
        });
      }
    }
  );

  const handleToolbarToggle = useCallback(() => {
    const next = !toolbarVisible;
    updateComposerToolbarVisibleMutation({
      variables: { visible: next },
      optimisticResponse: {
        updateComposerToolbarVisible: {
          __typename: 'UserProfile',
          username: profileData?.getMyProfile?.username || '',
          composerToolbarVisible: next,
          updatedAt: new Date().toISOString()
        }
      }
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[composer-v2] Failed to persist toolbar visibility:', err);
    });
  }, [toolbarVisible, updateComposerToolbarVisibleMutation, profileData]);

  // KAP-303 — async member search invoked by BeautifulMentionsPlugin via
  // its `onSearch(trigger, query)` prop. We hit `GET_CONVERSATION_MEMBERS`
  // through the Apollo client (cache-first so repeated queries within a
  // single mention session don't refetch) and shape each candidate as
  // `{ value: displayName, data: { username, displayName } }` so the
  // mention transformer (KAP-303 commit 5) can serialize using the
  // user's id as the sentinel UUID. The trailing "Kapsa AI" entry
  // matches V1 textarea behavior so `@ka` resolves to the agent.
  const searchMembers = useCallback(
    async (_trigger, queryString) => {
      if (!conversationId) return [];
      const q = (queryString || '').toLowerCase();
      try {
        const result = await apolloClient.query({
          query: GET_CONVERSATION_MEMBERS,
          variables: { conversationId },
          fetchPolicy: 'cache-first'
        });
        const raw = result.data?.getConversationMembers || [];
        const items = raw
          .map((m) => {
            // Some users' DDB row carries an email-shaped displayName
            // (Google OAuth sets it to the email when no custom name is
            // provided). Showing "@yvonne@kapsa.ai" in the mention text
            // is noisy; derive a friendly label from the email's
            // local part instead.
            const displayName = prettifyMentionLabel(m.displayName, m.userId);
            return {
              value: displayName,
              // KAP-303 fix-forward: lexical-beautiful-mentions stores ALL
              // non-`value` properties FLAT on `node.__data`. Nesting them
              // inside a `data: {...}` wrapper double-wraps the metadata
              // (node.getData() returns `{ data: { id: ... } }` instead of
              // `{ id: ... }`) — and SentinelMentionNode.getTextContent's
              // sentinel emission silently falls back to the bare-`@value`
              // branch. Caught via live editor-state probe against dev.
              id: m.userId,
              username: m.userId,
              displayName
            };
          })
          .concat([
            {
              value: 'Kapsa AI',
              id: 'KapsaAI',
              username: 'KapsaAI',
              displayName: 'Kapsa AI'
            }
          ])
          .filter((item) => {
            if (!q) return true;
            // KAP-303 fix-forward: items are FLAT (no `data` wrapper) per
            // the comment above. The previous filter read `item.data.username`
            // which threw and silently returned [] from the catch — making
            // mention autocomplete invisible the moment the user typed any
            // character after `@`. Caught via live smoke. Read flat.
            const display = item.value.toLowerCase();
            const handle = (item.username || '').toLowerCase();
            const first = display.split(/\s+/)[0];
            return display.startsWith(q) || handle.startsWith(q) || first.startsWith(q);
          })
          .slice(0, 8);
        return items;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[composer-v2] mention search failed:', err);
        return [];
      }
    },
    [apolloClient, conversationId]
  );

  const handleMentionMenuOpen = useCallback(() => {
    mentionMenuOpenRef.current = true;
  }, []);
  const handleMentionMenuClose = useCallback(() => {
    mentionMenuOpenRef.current = false;
  }, []);

  const [runUploadUrlMutation] = useMutation(GET_UPLOAD_URL);

  const [sendMessage, { loading: sending }] = useMutation(SEND_MESSAGE_V2, {
    update(cache, { data }) {
      if (!data?.sendMessage) return;
      const newMsg = data.sendMessage;
      try {
        const existing = cache.readQuery({
          query: GET_MESSAGES_PAGINATED,
          variables: { conversationId, limit: 50 }
        });
        if (existing?.getMessagesPaginated) {
          cache.writeQuery({
            query: GET_MESSAGES_PAGINATED,
            variables: { conversationId, limit: 50 },
            data: {
              getMessagesPaginated: {
                ...existing.getMessagesPaginated,
                messages: mergeMessageIntoCache(
                  existing.getMessagesPaginated.messages || [],
                  newMsg
                )
              }
            }
          });
        }
      } catch (e) {
        // Cache miss is fine
      }
      if (threadParentId) {
        try {
          const existingThread = cache.readQuery({
            query: GET_THREAD_REPLIES,
            variables: { messageId: threadParentId, limit: 50 }
          });
          if (existingThread?.getThreadReplies) {
            cache.writeQuery({
              query: GET_THREAD_REPLIES,
              variables: { messageId: threadParentId, limit: 50 },
              data: {
                getThreadReplies: {
                  ...existingThread.getThreadReplies,
                  messages: [...existingThread.getThreadReplies.messages, newMsg]
                }
              }
            });
          }
        } catch (e) {
          // Cache miss is fine
        }
      }
    }
  });

  const defaultPlaceholder = isThread
    ? 'Reply in thread...'
    : channelName
      ? `Message #${channelName}`
      : 'Type a message...';
  const placeholderText = customPlaceholder || defaultPlaceholder;

  // ---- Attachment handlers (duplicated from V1; KAP-303 will extract) ----

  const startUpload = useCallback(
    async (entryId, file) => {
      try {
        const attachment = await uploadAttachment({
          file,
          conversationId,
          runUploadUrlMutation,
          onProgress: (progress) => {
            setPendingAttachments((entries) =>
              entries.map((e) =>
                e.id === entryId && e.status === 'uploading' ? { ...e, progress } : e
              )
            );
          }
        });
        setPendingAttachments((entries) =>
          entries.map((e) =>
            e.id === entryId ? { ...e, status: 'done', progress: 1, attachment } : e
          )
        );
      } catch (err) {
        setPendingAttachments((entries) =>
          entries.map((e) =>
            e.id === entryId
              ? { ...e, status: 'error', errorMessage: err.message || 'Upload failed.' }
              : e
          )
        );
      }
    },
    [conversationId, runUploadUrlMutation]
  );

  const processFiles = useCallback(
    (fileList) => {
      setAttachmentError('');
      if (!fileList || fileList.length === 0) return;
      const remainingSlots = MAX_ATTACHMENTS_PER_MESSAGE - pendingAttachments.length;
      if (remainingSlots <= 0) {
        setAttachmentError(`Maximum of ${MAX_ATTACHMENTS_PER_MESSAGE} attachments per message.`);
        return;
      }
      const accepted = fileList.slice(0, remainingSlots);
      const rejected = fileList.length > remainingSlots
        ? `Only the first ${remainingSlots} of ${fileList.length} files were added — limit is ${MAX_ATTACHMENTS_PER_MESSAGE}.`
        : '';
      const newEntries = [];
      for (const file of accepted) {
        const reason = rejectionReasonFor(file);
        const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        if (reason) {
          newEntries.push({ id, file, status: 'error', progress: 0, errorMessage: reason });
          continue;
        }
        const kind = classifyAttachmentKind(file.type);
        const previewUrl = kind === 'image' ? URL.createObjectURL(file) : null;
        newEntries.push({ id, file, status: 'uploading', progress: 0, previewUrl, kind });
      }
      setPendingAttachments((entries) => [...entries, ...newEntries]);
      if (rejected) setAttachmentError(rejected);
      newEntries.filter((e) => e.status === 'uploading').forEach((e) => startUpload(e.id, e.file));
    },
    [pendingAttachments.length, startUpload]
  );

  const handleFilesPicked = useCallback(
    (event) => {
      const fileList = Array.from(event.target.files || []);
      if (event.target) event.target.value = '';
      processFiles(fileList);
    },
    [processFiles]
  );

  const isFilesDrag = (event) => {
    const types = event?.dataTransfer?.types;
    if (!types) return false;
    for (let i = 0; i < types.length; i += 1) {
      if (types[i] === 'Files') return true;
    }
    return false;
  };

  const handleDragEnter = useCallback((event) => {
    if (!isFilesDrag(event)) return;
    event.preventDefault();
    dragCounterRef.current += 1;
    setIsDraggingOver(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!isFilesDrag(event)) return;
    event.preventDefault();
  }, []);

  const handleDragLeave = useCallback((event) => {
    if (!isFilesDrag(event)) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    (event) => {
      if (!isFilesDrag(event)) return;
      event.preventDefault();
      dragCounterRef.current = 0;
      setIsDraggingOver(false);
      if (sending) {
        setAttachmentError('Wait for the current message to send before adding files.');
        return;
      }
      const fileList = Array.from(event.dataTransfer?.files || []);
      processFiles(fileList);
    },
    [sending, processFiles]
  );

  // Clipboard paste — mirror drag-drop semantics for image/file attachments.
  // Browsers expose pasted screenshots via `clipboardData.items` with
  // `kind === 'file'`; we route those through the same `processFiles` pipeline
  // that the drag-drop and file-picker code paths use, so MIME validation,
  // slot limits, previews, and upload all stay consistent. Plain-text and
  // HTML pastes are not intercepted — they fall through to Lexical so
  // typing flows unchanged.
  const handlePaste = useCallback(
    (event) => {
      const items = event.clipboardData?.items;
      if (!items || items.length === 0) return;
      const fileList = [];
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) fileList.push(file);
        }
      }
      if (fileList.length === 0) return;
      event.preventDefault();
      if (sending) {
        setAttachmentError('Wait for the current message to send before adding files.');
        return;
      }
      processFiles(fileList);
    },
    [sending, processFiles]
  );

  const removeAttachment = useCallback((entryId) => {
    setPendingAttachments((entries) => {
      const target = entries.find((e) => e.id === entryId);
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return entries.filter((e) => e.id !== entryId);
    });
  }, []);

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current && !sending) fileInputRef.current.click();
  }, [sending]);

  const hasUploadInFlight = pendingAttachments.some((e) => e.status === 'uploading');
  const hasUploadError = pendingAttachments.some((e) => e.status === 'error');
  const completedAttachments = pendingAttachments
    .filter((e) => e.status === 'done' && e.attachment)
    .map((e) => e.attachment);

  const canSend =
    !sending &&
    !hasUploadInFlight &&
    !hasUploadError &&
    (value.trim().length > 0 || completedAttachments.length > 0);

  const doSend = useCallback(async () => {
    if (!canSend) return;
    const trimmed = value.trim();
    const attachmentsToSend = completedAttachments;
    const previousAttachmentsRefs = [...pendingAttachments];

    setValue('');
    setPendingAttachments([]);
    setAttachmentError('');
    setEditorResetCount((c) => c + 1);

    try {
      await sendMessage({
        variables: {
          conversationId,
          content: trimmed,
          threadParentId: threadParentId || null,
          alsoPostToChannel: isThread ? alsoPostToChannel : null,
          attachments: attachmentsToSend.length > 0 ? attachmentsToSend : null
        }
      });
      // KAP-312: signal the message list to scroll to bottom on next render.
      document.dispatchEvent(new Event('kapsa-own-message-sent'));
      previousAttachmentsRefs.forEach((e) => {
        if (e.previewUrl) URL.revokeObjectURL(e.previewUrl);
      });
      onAssistantThinkingStart?.(trimmed);
    } catch (err) {
      // Restore state on failure. Note: the editor was already cleared via
      // `editorResetCount`; the user can retry by retyping or via the
      // attachment list (which we restore here).
      setValue(trimmed);
      setPendingAttachments(previousAttachmentsRefs);
      setAttachmentError(err?.message || 'Failed to send message.');
      // eslint-disable-next-line no-console
      console.error('Failed to send message:', err);
    }
  }, [
    canSend,
    value,
    completedAttachments,
    pendingAttachments,
    sendMessage,
    conversationId,
    threadParentId,
    isThread,
    alsoPostToChannel,
    onAssistantThinkingStart
  ]);

  const handleSubmit = (e) => {
    e.preventDefault();
    doSend();
  };

  // KAP-303 — `lexical-beautiful-mentions` owns arrow / Enter / Tab / Esc
  // when its menu is open, and the editor owns Enter via `EnterToSendPlugin`
  // when the menu is closed (gated on `mentionMenuOpenRef`). The form-level
  // keydown shim that pre-empted these keys for the legacy MentionAutocomplete
  // is no longer needed.

  return (
    <form
      className={`msg-composer${isDraggingOver ? ' msg-composer--dragover' : ''} msg-composer-v2`}
      onSubmit={handleSubmit}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onPaste={handlePaste}
    >
      <div className="msg-composer-wrapper">
        {pendingAttachments.length > 0 && (
          <ul className="msg-composer-attachment-list" aria-label="Attachments">
            {pendingAttachments.map((entry) => (
              <li
                key={entry.id}
                className={`msg-composer-attachment msg-composer-attachment--${entry.status}`}
              >
                {entry.previewUrl ? (
                  <img
                    src={entry.previewUrl}
                    alt={entry.file.name}
                    className="msg-composer-attachment-preview"
                  />
                ) : (
                  <span className="msg-composer-attachment-icon" aria-hidden="true">📄</span>
                )}
                <span className="msg-composer-attachment-name" title={entry.file.name}>
                  {entry.file.name}
                </span>
                {entry.status === 'uploading' && (
                  <span className="msg-composer-attachment-progress" aria-live="polite">
                    {Math.round((entry.progress || 0) * 100)}%
                  </span>
                )}
                {entry.status === 'error' && (
                  <span className="msg-composer-attachment-error" role="alert">
                    {entry.errorMessage}
                  </span>
                )}
                <button
                  type="button"
                  className="msg-composer-attachment-remove"
                  aria-label={`Remove ${entry.file.name}`}
                  onClick={() => removeAttachment(entry.id)}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        <LexicalComposer initialConfig={LEXICAL_INITIAL_CONFIG}>
          <LinkDialogProvider>
            <ComposerToolbar visible={toolbarVisible} />
            <div className="msg-composer-v2-input">
              <RichTextPlugin
                contentEditable={
                  <ContentEditable
                    className="msg-composer-textarea msg-composer-v2-editable"
                    aria-label="Type a message"
                    data-composer-v2="true"
                  />
                }
                placeholder={
                  <div className="msg-composer-v2-placeholder">{placeholderText}</div>
                }
                ErrorBoundary={LexicalErrorBoundary}
              />
              <HistoryPlugin />
              <LinkPlugin />
              <AutoLinkPlugin matchers={URL_MATCHERS} />
              <ListPlugin />
              <TabIndentationPlugin />
              <CodeHighlightPlugin />
              <MarkdownShortcutPlugin transformers={COMPOSER_TRANSFORMERS} />
              <KeyboardShortcutsPlugin />
              <EditorChangePlugin onContentChange={setValue} />
              {/* KAP-303 — owns mention trigger detection, async member
                  fetch, menu rendering, item insertion. Menu inherits
                  `mention-autocomplete*` classes from MessageComposer.css
                  so V1 and V2 look identical. searchDelay=150 mirrors the
                  previous setMentionQuery debounce. */}
              <BeautifulMentionsPlugin
                triggers={["@"]}
                onSearch={searchMembers}
                searchDelay={150}
                menuComponent={MentionMenu}
                menuItemComponent={MentionMenuItem}
                onMenuOpen={handleMentionMenuOpen}
                onMenuClose={handleMentionMenuClose}
              />
              <EmojiInsertPlugin
                pendingEmoji={pendingEmojiInsert}
                onConsumed={() => setPendingEmojiInsert(null)}
              />
              <MentionTriggerInsertPlugin
                pendingTrigger={pendingMentionTrigger}
                onConsumed={() => setPendingMentionTrigger(null)}
              />
              <EnterToSendPlugin onSend={doSend} mentionMenuOpenRef={mentionMenuOpenRef} />
              <ClearEditorPlugin resetCount={editorResetCount} />
            </div>
            <CodeBlockLanguagePicker />
            <LinkPopover />
          </LinkDialogProvider>
        </LexicalComposer>

        {attachmentError && (
          <div className="msg-composer-attachment-toast" role="alert">
            {attachmentError}
          </div>
        )}

        {/* KAP-308 follow-up — bottom-actions row lives INSIDE the editor
            card now (Slack-style), not as a sibling column to the right.
            Button order matches Slack literally: [+] [Aa] [😊]
            left-aligned, optional thread checkbox after, [Send] floated
            right via `margin-left: auto` in `MessageComposerV2.css`. */}
        <div className="msg-composer-actions msg-composer-actions--v2">
          <AttachMenu
            openFilePicker={openFilePicker}
            disabled={sending || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
          />
          <button
            type="button"
            className={`msg-composer-aa${toolbarVisible ? ' is-pressed' : ''}`}
            onClick={handleToolbarToggle}
            aria-label={toolbarVisible ? 'Hide formatting toolbar' : 'Show formatting toolbar'}
            aria-pressed={toolbarVisible}
            title="Toggle formatting toolbar"
          >
            <span aria-hidden="true">Aa</span>
          </button>
          <ComposerEmojiButton
            onSelectEmoji={(glyph) => setPendingEmojiInsert({ glyph, requestedAt: Date.now() })}
            disabled={sending}
          />
          <button
            type="button"
            className="msg-composer-mention-btn"
            onMouseDown={(event) => {
              // Match the formatting toolbar buttons: prevent focus from
              // moving to this button so the editor's selection survives
              // the click — `OPEN_MENTION_MENU_COMMAND` operates against
              // whatever selection was active when the user clicked.
              event.preventDefault();
            }}
            onClick={() => setPendingMentionTrigger({ requestedAt: Date.now() })}
            disabled={sending}
            aria-label="Mention someone"
            title="Mention someone"
          >
            <span aria-hidden="true">@</span>
          </button>
          {isThread && (
            <label className="msg-composer-also-post">
              <input
                type="checkbox"
                checked={alsoPostToChannel}
                onChange={(e) => setAlsoPostToChannel(e.target.checked)}
              />
              <span>
                {channelName ? <>Also send to <strong>#{channelName}</strong></> : 'Also send to channel'}
              </span>
            </label>
          )}
          <button
            type="submit"
            className="msg-composer-send"
            disabled={!canSend}
            aria-label="Send message"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false">
              <path
                d="M12 5L12 16M7.5 9.5L12 5L16.5 9.5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
              />
            </svg>
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ALLOWED_ATTACHMENT_ACCEPT}
        className="msg-composer-file-input"
        onChange={handleFilesPicked}
        aria-hidden="true"
        tabIndex={-1}
      />

      {isDraggingOver && (
        <div className="msg-composer-drop-overlay" aria-hidden="true">
          <span className="msg-composer-drop-overlay-text">
            Drop image or PDF to attach
          </span>
        </div>
      )}
    </form>
  );
}

export default MessageComposerV2;
